// 📦 utils/shopify.js

// 👉 Set your domain and token
export const SHOPIFY_STORE_DOMAIN = "thetacogarage.myshopify.com";
const SHOPIFY_STOREFRONT_ACCESS_TOKEN = "3fe64e1e444cbe10cc6d6715fde9e4f0";

const SHOPIFY_API_URL = `https://${SHOPIFY_STORE_DOMAIN}/api/2023-10/graphql.json`;

// ----------------------
// Low-level GraphQL request
// ----------------------
export async function shopifyAPI(query, variables = {}) {
  const res = await fetch(SHOPIFY_API_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Shopify-Storefront-Access-Token": SHOPIFY_STOREFRONT_ACCESS_TOKEN,
    },
    body: JSON.stringify({ query, variables }),
  });

  if (!res.ok) {
    throw new Error(`Shopify API error: ${res.status}`);
  }
  const json = await res.json();
  if (json.errors) {
    throw new Error(`GraphQL errors: ${JSON.stringify(json.errors)}`);
  }
  return json.data;
}

// ------------------------------------
// Shared cart fields
// ------------------------------------
const CART_FRAGMENT = `
fragment CartFields on Cart {
  id
  checkoutUrl
  lines(first: 50) {
    edges {
      node {
        id
        quantity
        cost {
          amountPerQuantity { amount currencyCode }
          subtotalAmount { amount currencyCode }
          totalAmount { amount currencyCode }
        }
        merchandise {
          ... on ProductVariant {
            id
            title
            priceV2 { amount currencyCode }
            product {
              title
              images(first: 1) {
                edges { node { url altText } }
              }
            }
          }
        }
      }
    }
  }
  estimatedCost {
    subtotalAmount { amount currencyCode }
    totalAmount { amount currencyCode }
  }
}
`;

// Helper for variant IDs
const toVariantGid = (id) =>
  String(id).startsWith("gid://") ? id : `gid://shopify/ProductVariant/${id}`;

// ----------------------
// Cart Operations
// ----------------------
export async function createCart() {
  const mutation = `
    mutation {
      cartCreate {
        cart { ...CartFields }
      }
    }
    ${CART_FRAGMENT}
  `;
  const data = await shopifyAPI(mutation);
  return data.cartCreate.cart;
}

export async function getCart(cartId) {
  const query = `
    query getCart($cartId: ID!) {
      cart(id: $cartId) { ...CartFields }
    }
    ${CART_FRAGMENT}
  `;
  const data = await shopifyAPI(query, { cartId });
  return data.cart;
}

export async function addToCart(cartId, variantId, quantity = 1) {
  const mutation = `
    mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
      cartLinesAdd(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
      }
    }
    ${CART_FRAGMENT}
  `;
  const variables = {
    cartId,
    lines: [{ merchandiseId: toVariantGid(variantId), quantity }],
  };
  const data = await shopifyAPI(mutation, variables);
  return data.cartLinesAdd.cart;
}

export async function updateCartItem(cartId, lineId, quantity) {
  const mutation = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart { ...CartFields }
      }
    }
    ${CART_FRAGMENT}
  `;
  const variables = { cartId, lines: [{ id: lineId, quantity }] };
  const data = await shopifyAPI(mutation, variables);
  return data.cartLinesUpdate.cart;
}

export async function removeCartItem(cartId, lineId) {
  const mutation = `
    mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
      cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
        cart { ...CartFields }
      }
    }
    ${CART_FRAGMENT}
  `;
  const variables = { cartId, lineIds: [lineId] };
  const data = await shopifyAPI(mutation, variables);
  return data.cartLinesRemove.cart;
}

// ----------------------
// Product Helpers
// ----------------------
function transformProduct(product) {
  const images =
    product.images?.edges?.map((e) => ({
      url: e.node.url,
      altText: e.node.altText,
    })) || [];

  const variants =
    product.variants?.edges?.map((e) => ({
      id: e.node.id,
      title: e.node.title,
      availableForSale: e.node.availableForSale,
      price: parseFloat(e.node.price?.amount || 0),
      image: e.node.image?.url || null,
    })) || [];

  const firstAvailable =
    variants.find((v) => v.availableForSale) || variants[0];

  const selectedImage =
    firstAvailable?.image ||
    images[0]?.url ||
    "/placeholder.png";

  const options =
    product.options?.map((opt) => ({
      name: opt.name,
      values: opt.values || [],
    })) || [];

  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    description: product.description || "",
    options,
    category: product.productType || "Uncategorized",
    tags: (product.tags || []).map((t) => t.toLowerCase()),
    price: firstAvailable?.price || 0,
    images,
    variants,
    variantId: firstAvailable?.id || null,
    selectedImage,
    image: selectedImage,
    imageAlt: images[0]?.altText || product.title,
    availableForSale: product.availableForSale,
  };
}

export const shopifyHelpers = {
  getAllProducts: async (limit = 250) => {
    let products = [];
    let hasNextPage = true;
    let cursor = null;

    while (hasNextPage) {
      const query = `
        query GetProducts($first: Int!, $after: String) {
          products(first: $first, after: $after) {
            edges {
              node {
                id
                title
                handle
                description
                productType
                tags
                availableForSale
                priceRange { minVariantPrice { amount currencyCode } }
                images(first: 10) {
                  edges {
                    node {
                      url
                      altText
                    }
                  }
                }
                variants(first: 20) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price { amount currencyCode }
                    }
                  }
                }
              }
              cursor
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      `;
      const data = await shopifyAPI(query, { first: limit, after: cursor });
      const edges = data.products.edges;
      products = [...products, ...edges.map((e) => transformProduct(e.node))];
      hasNextPage = data.products.pageInfo.hasNextPage;
      cursor = data.products.pageInfo.endCursor;
    }

    return products;
  },

  getProductByHandle: async (handle) => {
    const query = `
      query GetProduct($handle: String!) {
        product(handle: $handle) {
          id
          title
          handle
          description
          productType
          tags
          availableForSale
          options {
            name
            values
          }
          images(first: 10) {
            edges {
              node {
                url
                altText
              }
            }
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                availableForSale
                image {
                  url
                  altText
                }
                price {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      }
    `;
    const data = await shopifyAPI(query, { handle });
    return transformProduct(data.product);
  },
};
